import { queryOptions, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Permission } from '@/api/permissions';
import { setSentryUser } from '@/utils/sentry/sentry-user';

import { apiUserDashboardsFetch } from '..';
import { UserGlobalRole } from '../auth/mutations';
import type { BadgeCount } from '../chat/v2-types';
import { FundApprovalStatus } from '../fund/types';
import { TeamAssociateStatusType } from '../types';

export const USER_DASHBOARD_QUERY_KEYS = {
  affiliations: 'affiliations',
  userProfile: 'user-profile',
  entities: 'user-dashboard-entities',
  ownerships: 'ownerships',
  chatBadgeCount: 'chat-badge-count',
};

export type UserFundAffiliation = {
  fundName: string;
  fundId: string;
  fundMemberId: string | null;
  fundMemberInvitation: string | null;
  title: string | null;
  memberSince: string | null;
  type: string;
  fundApprovalStatus: FundApprovalStatus;
  slug: string | null;
};

export type Affiliation = {
  entityId: string;
  entityMemberId: string | null;
  entityInvitationId: string | null;
  entityName: string;
  entityDisplayName: string;
  isDirector: boolean;
  isApproved: boolean;
  role: string;
  status: TeamAssociateStatusType;
  fundAffiliations: UserFundAffiliation[];
};

export type AffiliationsRes = {
  entityAffiliations: Affiliation[];
};

export const _getAffiliations = () => {
  return queryOptions({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.affiliations],
    queryFn: () => apiUserDashboardsFetch<AffiliationsRes>('/affiliations'),
    refetchOnMount: true,
  });
};

export type UserProfileRes = {
  id: string;
  email: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  description: string | null;
  cvFileId: string | null;
  headshotFileId: string | null;
  linkedinProfile: string | null;
  xProfile: string | null;
  countryOfResidence: string | null;
  nationalities: string[];
  phoneNumber: string | null;
  address: string | null;
  company: string | null;
  walletAddress: string | null;
  kycStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'IN_PROGRESS';
  verifiedAt: string | null;
  role: UserGlobalRole;
  isDemo: boolean;
  hasSubmittedQuestionnaire: boolean;
  referralCode: string | null;
  qrCode: string | null;
  /**
   * Session-scoped: the backend derives this from the caller's global role plus the
   * entity role of the currently active session (resolved per-request from the JWT).
   * Anything that re-mints the JWT must invalidate this query — see `useRefreshSession`.
   */
  permissions: Permission[];
  /** `null` is full view; an array is the granted address-book category ids. */
  addressBookCategoryScope: string[] | null;
  profileCompleted: boolean;
  isBlocked: boolean;
  blockReason: string | null;
  isReferralAgreementSigned: boolean;
  /**
   * isNonAccredited is true when the user is located in a country that requires
   * accredited-investor status (currently: US) AND does not meet that country's
   * accreditation criteria. false for all other cases — including non-US users
   * and US-accredited users.
   */
  isNonAccredited: boolean;
};

export const _getUserProfile = () => {
  return queryOptions({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile],
    queryFn: async () => {
      const data = await apiUserDashboardsFetch<UserProfileRes>('/profile');
      setSentryUser({ id: data.id, email: data.email, role: data.role });
      return data;
    },
    // While the API is unreachable this query errors with no data, and the boot
    // gates (CognitoSessionGate, AuthLayout, MainLayout) remount consumers on
    // every pending/error flip. Each remount would refetch, flipping the query
    // back to pending — an unbounded request loop against the dead backend.
    // Recovery still happens via window focus, reconnect, and explicit
    // invalidation on sign-in/session refresh.
    retryOnMount: false,
  });
};

export function useGetUserProfile() {
  const { data: user, ...rest } = useQuery(_getUserProfile());
  const queryClient = useQueryClient();
  const isInvalidated =
    queryClient.getQueryState([USER_DASHBOARD_QUERY_KEYS.userProfile])?.isInvalidated ?? false;

  return { user, ...rest, isInvalidated, isDemo: !!user?.isDemo };
}

export function useGetUserProfileSuspense() {
  const { data: user, ...rest } = useSuspenseQuery(_getUserProfile());

  return { user, ...rest, isDemo: !!user?.isDemo };
}

export type UserEntity = {
  entityId: string;
  entityName: string;
  legalName: string | null;
  logoUrl: string | null;
  country: string | null;
  state: string | null;
  type: string | null;
  funds: string | null;
  entityApprovalStatus: string;
  blockReason: string | null;
  entityRole: string;
  createdAt: string;
  updatedAt: string;
};

export type UserEntitiesRes = {
  entities: UserEntity[];
};
export const _getUserEntities = () => {
  return queryOptions({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.entities],
    queryFn: () => apiUserDashboardsFetch<UserEntitiesRes>('/entities'),
  });
};

export type OwnershipInEntity = {
  class: string;
  units: number;
  percentOfTotal: number;
};

export type OwnershipEntity = {
  entityId: string;
  entityName: string;
  ownerships: OwnershipInEntity[];
};

export type OwnershipsRes = {
  entities: OwnershipEntity[];
};

export const _getOwnerships = () => {
  return queryOptions({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.ownerships],
    queryFn: () => apiUserDashboardsFetch<OwnershipsRes>('/ownerships'),
    refetchOnMount: true,
    retry: 1,
  });
};

export type EntityBadgeCount = BadgeCount['entities'][number];
export type ChatBadgeCountRes = BadgeCount;

export const _getChatBadgeCount = () => {
  return queryOptions({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.chatBadgeCount],
    queryFn: () => apiUserDashboardsFetch<ChatBadgeCountRes>('/chat-badge-count'),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
};

export function useGetChatBadgeCount() {
  return useQuery(_getChatBadgeCount());
}
